import { useEffect, useState } from 'react';
import {
  fetchFactionProfiles,
  fetchForcePresets,
  type FactionProfileView,
  type ForcePresetView,
} from '../api/catalog';
import { ApiError } from '../api/http';
import { rerollCastName, rerollStory } from '../api/missions';
import { addCast, canRemove, editCast, removeCast, renameCast } from './castEdit';
import type {
  AreaOfOperations,
  CivilianZone,
  Definition,
  DefinitionRefusal,
  PreflightReport,
  PreflightRow,
  Force,
  IntelNote,
  MapMarker,
  MissionView,
  OptionalView,
  PlacedComposition,
  RecipeConstraints,
  RestrictedZone,
  Twist,
} from '../api/missions';
import { ca } from '../i18n/ca';
import {
  checks,
  LAYERS,
  newCivilianZone,
  newIntel,
  newMarker,
  newRestrictedZone,
  newTwist,
  objectiveLabel,
  RESPAWN_MODES,
  respawnNode,
  respawnOf,
  shapeCenter,
  type Check,
  type Layer,
  type LayerState,
  type Selection,
  type Tool,
} from './designModel';
import { MissionCover } from './MissionCover';
import { COVERS, type Cover } from './missionCovers';
import { NatoSymbol } from './NatoSymbol';
import { RecipeParams } from './RecipeParams';
import {
  activeGroups,
  alarmScenes,
  maxActiveGroups,
  optionalOn,
  sceneLabel,
  scriptedForceIds,
} from './scriptWords';
import { mayAdd, pathRule, ruleFor, scripted as isScripted } from './editRules';
import { COLOUR_CHOICES, colourOf, PLAIN, ROLES, SIDES } from './natoSymbols';

/**
 * The designer's inspector panels: one per thing a mission is made of, each a
 * plain form over the definition.
 *
 * They never talk to the platform. Every change goes through `update`, which is
 * the designer's own "change the document I hold"; saving, patching and the
 * live channel stay where they were. That keeps these components testable with
 * nothing but a definition and makes "what does this control write" answerable
 * by reading one function.
 */

const d = ca.design;

interface PanelProps {
  def: Definition;
  update: (patch: (def: Definition) => Definition) => void;
  selected: Selection | null;
  select: (selection: Selection | null) => void;
  setTool: (tool: Tool) => void;
}

/** A labelled number input that writes a number, never a NaN. */
function Num({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number | undefined;
  min?: number;
  max?: number;
  step?: number;
  onChange: (n: number) => void;
}) {
  return (
    <label>
      {label}
      <input
        type="number"
        value={value ?? ''}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
      />
    </label>
  );
}

function Choice({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string | undefined;
  options: [string, string][];
  onChange: (v: string) => void;
}) {
  return (
    <label>
      {label}
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        {options.map(([v, name]) => (
          <option key={v} value={v}>
            {name}
          </option>
        ))}
      </select>
    </label>
  );
}

/** The colours a marker may take, as swatches rather than a list of class names. */
function ColourPicker({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (c: string) => void;
}) {
  return (
    <div className="swatches" role="group" aria-label={d.markerColour}>
      {COLOUR_CHOICES.map((c) => (
        <button
          key={c}
          type="button"
          className={`swatch ${value === c ? 'on' : ''}`}
          style={{ background: colourOf(c) }}
          aria-label={c}
          aria-pressed={value === c}
          title={c}
          onClick={() => onChange(c)}
        />
      ))}
    </div>
  );
}

/** The NATO symbol picker: a side, a role, and the plain marks below. */
export function SymbolPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (symbol: string) => void;
}) {
  const [side, setSide] = useState<string>(/^([bonu])_/.exec(value)?.[1] ?? 'o');
  return (
    <div className="symbol-picker">
      <div className="row tight" role="group" aria-label={d.markerSides}>
        {SIDES.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`chip toggle ${side === s.key ? 'on' : ''}`}
            aria-pressed={side === s.key}
            onClick={() => setSide(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div className="symbol-grid" role="group" aria-label={d.markerRoles}>
        {ROLES.map((r) => {
          const symbol = `${side}_${r.key}`;
          return (
            <button
              key={symbol}
              type="button"
              className={`symbol ${value === symbol ? 'on' : ''}`}
              aria-pressed={value === symbol}
              title={r.label}
              onClick={() => onChange(symbol)}
            >
              <NatoSymbol symbol={symbol} size={30} />
              <span>{r.label}</span>
            </button>
          );
        })}
      </div>
      <p className="muted small">{d.markerPlain}</p>
      <div className="symbol-grid" role="group" aria-label={d.markerPlain}>
        {PLAIN.map((p) => (
          <button
            key={p.key}
            type="button"
            className={`symbol ${value === p.key ? 'on' : ''}`}
            aria-pressed={value === p.key}
            title={p.label}
            onClick={() => onChange(p.key)}
          >
            <NatoSymbol symbol={p.key} colour="ColorBlack" size={30} />
            <span>{p.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** The layer switches and the tool palette that sit over the map. */
export function MapChrome({
  layers,
  setLayers,
  tool,
  setTool,
  grid,
  setGrid,
}: {
  layers: LayerState;
  setLayers: (l: LayerState) => void;
  tool: Tool;
  setTool: (t: Tool) => void;
  grid: number;
  setGrid: (n: number) => void;
}) {
  const tools: Tool[] = [
    'select',
    'marker',
    'intel',
    'patrol',
    'garrison',
    'guard',
    'static',
    'vehicle',
    'civilian',
    'composition',
    'area',
    'zone',
  ];
  return (
    <div className="map-chrome">
      <div className="toolbar" role="group" aria-label={d.tools}>
        {tools.map((t) => (
          <button
            key={t}
            type="button"
            className={`chip toggle ${tool === t ? 'on' : ''}`}
            aria-pressed={tool === t}
            title={d.toolHints[t]}
            onClick={() => setTool(t)}
          >
            {d.toolNames[t]}
          </button>
        ))}
      </div>
      <div className="toolbar" role="group" aria-label={d.layers}>
        {LAYERS.map((l: Layer) => (
          <button
            key={l}
            type="button"
            className={`chip toggle small ${layers[l] ? 'on' : ''}`}
            aria-pressed={layers[l]}
            onClick={() => setLayers({ ...layers, [l]: !layers[l] })}
          >
            {d.layerNames[l]}
          </button>
        ))}
        <span className="grow" />
        <label className="inline">
          {ca.mapUi.grid}
          <select value={grid} onChange={(e) => setGrid(Number(e.target.value))}>
            <option value={0}>{d.none}</option>
            <option value={500}>500 m</option>
            <option value={1000}>1 km</option>
            <option value={2000}>2 km</option>
          </select>
        </label>
      </div>
      <p className="muted small">{d.toolHints[tool]}</p>
    </div>
  );
}

// ---------------------------------------------------------------- Àrea

export function AreaPanel({ def, update, selected, select, setTool }: PanelProps) {
  const area = def.area;
  const setArea = (patch: Partial<AreaOfOperations>) =>
    update((x) => ({ ...x, area: { ...(x.area as AreaOfOperations), ...patch } }));
  const zones = area?.restricted ?? [];
  const setZone = (i: number, patch: Partial<RestrictedZone>) =>
    update((x) => ({
      ...x,
      area: {
        ...(x.area as AreaOfOperations),
        restricted: (x.area?.restricted ?? []).map((z, j) => (j === i ? { ...z, ...patch } : z)),
      },
    }));

  if (!area) {
    return (
      <section className="card" aria-label={d.areaPanel}>
        <h2>{d.areaPanel}</h2>
        <p className="muted small">{d.areaHint}</p>
        <p className="muted small">{d.areaNone}</p>
        <button type="button" onClick={() => setTool('area')}>
          {d.toolNames.area}
        </button>
      </section>
    );
  }
  const [cx, cy] = shapeCenter(area);
  return (
    <>
      <section className="card form" aria-label={d.areaPanel}>
        <h2>{d.areaPanel}</h2>
        <p className="muted small">{d.areaHint}</p>
        <label>
          {d.areaLabel}
          <input value={area.label ?? ''} onChange={(e) => setArea({ label: e.target.value })} />
        </label>
        <Choice
          label={d.areaShape}
          value={area.shape}
          options={[
            ['rect', d.shapeNames.rect],
            ['ellipse', d.shapeNames.ellipse],
          ]}
          onChange={(v) => setArea({ shape: v as AreaOfOperations['shape'] })}
        />
        <div className="two">
          <Num label={d.areaWidth} value={area.a} min={50} onChange={(n) => setArea({ a: n })} />
          <Num label={d.areaHeight} value={area.b} min={50} onChange={(n) => setArea({ b: n })} />
        </div>
        <Num
          label={d.areaAngle}
          value={area.angle ?? 0}
          min={-180}
          max={180}
          onChange={(n) => setArea({ angle: n })}
        />
        <p className="muted small">
          {d.areaCentre}: {Math.round(cx)}, {Math.round(cy)}
        </p>
        <label className="check">
          <input
            type="checkbox"
            checked={area.showToPlayers !== false}
            onChange={(e) => setArea({ showToPlayers: e.target.checked })}
          />
          {d.areaShowToPlayers}
        </label>
        <ColourPicker value={area.colour} onChange={(c) => setArea({ colour: c })} />
        <button
          type="button"
          className="danger"
          onClick={() => {
            update((x) => ({ ...x, area: undefined }));
            select(null);
          }}
        >
          {d.areaRemove}
        </button>
      </section>

      <section className="card form" aria-label={d.zonesPanel}>
        <h2>{d.zonesPanel}</h2>
        <p className="muted small">{d.zonesHint}</p>
        {zones.length === 0 && <p className="muted small">{d.zoneNone}</p>}
        {zones.map((z, i) => (
          <div
            className={`stack ${selected?.kind === 'zone' && selected.id === z.id ? 'selected' : ''}`}
            key={z.id ?? i}
          >
            <label>
              {d.areaLabel}
              <input
                value={z.label ?? ''}
                onFocus={() => select({ kind: 'zone', id: z.id ?? '' })}
                onChange={(e) => setZone(i, { label: e.target.value })}
              />
            </label>
            <div className="two">
              <Choice
                label={d.zoneKind}
                value={z.kind ?? 'nogo'}
                options={Object.entries(d.zoneKinds)}
                onChange={(v) => setZone(i, { kind: v as RestrictedZone['kind'] })}
              />
              <Choice
                label={d.areaShape}
                value={z.shape}
                options={[
                  ['rect', d.shapeNames.rect],
                  ['ellipse', d.shapeNames.ellipse],
                ]}
                onChange={(v) => setZone(i, { shape: v as RestrictedZone['shape'] })}
              />
            </div>
            <div className="two">
              <Num
                label={d.areaWidth}
                value={z.a}
                min={10}
                onChange={(n) => setZone(i, { a: n })}
              />
              <Num
                label={d.areaHeight}
                value={z.b}
                min={10}
                onChange={(n) => setZone(i, { b: n })}
              />
            </div>
            <Num
              label={d.areaAngle}
              value={z.angle ?? 0}
              min={-180}
              max={180}
              onChange={(n) => setZone(i, { angle: n })}
            />
            <button
              type="button"
              className="danger small"
              onClick={() =>
                update((x) => ({
                  ...x,
                  area: {
                    ...(x.area as AreaOfOperations),
                    restricted: (x.area?.restricted ?? []).filter((_, j) => j !== i),
                  },
                }))
              }
            >
              {d.remove}
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => {
            const zone = newRestrictedZone(def, cx, cy);
            update((x) => ({
              ...x,
              area: {
                ...(x.area as AreaOfOperations),
                restricted: [...(x.area?.restricted ?? []), zone],
              },
            }));
            select({ kind: 'zone', id: zone.id ?? '' });
          }}
        >
          {d.zoneAdd}
        </button>
      </section>
    </>
  );
}

// ---------------------------------------------------------------- Marcadors

export function MarkersPanel({ def, update, selected, select }: PanelProps) {
  const markers = def.markers ?? [];
  const current = selected?.kind === 'marker' ? markers.find((m) => m.id === selected.id) : null;
  const setMarker = (id: string, patch: Partial<MapMarker>) =>
    update((x) => ({
      ...x,
      markers: (x.markers ?? []).map((m) => (m.id === id ? { ...m, ...patch } : m)),
    }));

  return (
    <section className="card form" aria-label={d.markersPanel}>
      <h2>{d.markersPanel}</h2>
      <p className="muted small">{d.markersHint}</p>
      {markers.length === 0 && <p className="muted small">{d.markerNone}</p>}
      <ul className="plain picker">
        {markers.map((m) => (
          <li key={m.id}>
            <button
              type="button"
              className={`row-button ${current?.id === m.id ? 'on' : ''}`}
              onClick={() => select({ kind: 'marker', id: m.id })}
            >
              <NatoSymbol symbol={m.symbol ?? 'mil_dot'} colour={m.colour} size={22} />
              <span>{m.text || m.id}</span>
              {m.audience === 'staff' && <span className="chip">{d.markerAudiences.staff}</span>}
            </button>
          </li>
        ))}
      </ul>

      {current && (
        <div className="stack">
          <label>
            {d.markerText}
            <input
              value={current.text ?? ''}
              maxLength={80}
              onChange={(e) => setMarker(current.id, { text: e.target.value })}
            />
          </label>
          <Choice
            label={d.markerKind}
            value={current.kind}
            options={Object.entries(d.markerKinds)}
            onChange={(v) => setMarker(current.id, { kind: v as MapMarker['kind'] })}
          />
          {current.kind === 'icon' && (
            <>
              <p className="muted small">{d.markerPickSymbol}</p>
              <SymbolPicker
                value={current.symbol ?? 'mil_dot'}
                onChange={(symbol) => setMarker(current.id, { symbol })}
              />
            </>
          )}
          {(current.kind === 'rect' || current.kind === 'ellipse') && (
            <div className="two">
              <Num
                label={d.areaWidth}
                value={current.a ?? 200}
                min={10}
                onChange={(n) => setMarker(current.id, { a: n })}
              />
              <Num
                label={d.areaHeight}
                value={current.b ?? 200}
                min={10}
                onChange={(n) => setMarker(current.id, { b: n })}
              />
            </div>
          )}
          <ColourPicker
            value={current.colour}
            onChange={(c) => setMarker(current.id, { colour: c })}
          />
          <div className="two">
            <Num
              label={d.markerSize}
              value={current.size ?? 1}
              min={0.2}
              max={4}
              step={0.1}
              onChange={(n) => setMarker(current.id, { size: n })}
            />
            <Num
              label={d.markerAlpha}
              value={current.alpha ?? 1}
              min={0}
              max={1}
              step={0.05}
              onChange={(n) => setMarker(current.id, { alpha: n })}
            />
          </div>
          <Choice
            label={d.markerAudience}
            value={current.audience ?? 'players'}
            options={Object.entries(d.markerAudiences)}
            onChange={(v) => setMarker(current.id, { audience: v as MapMarker['audience'] })}
          />
          <Choice
            label={d.markerReveal}
            value={current.revealAt ?? 'start'}
            options={[
              ['start', d.markerRevealStart],
              ...def.objectives.map(
                (o) =>
                  [`onObjective:${o.id}`, d.markerRevealOn(objectiveLabel(o))] as [string, string],
              ),
              // The generator marks each later order this way: drawn when it is given.
              ...def.objectives.map(
                (o) =>
                  [`withObjective:${o.id}`, d.markerRevealWith(objectiveLabel(o))] as [
                    string,
                    string,
                  ],
              ),
              ['never', d.markerRevealNever],
            ]}
            onChange={(v) => setMarker(current.id, { revealAt: v })}
          />
          <p className="muted small">
            {d.position}: {Math.round(current.position[0])}, {Math.round(current.position[1])}
          </p>
          <button
            type="button"
            className="danger"
            onClick={() => {
              update((x) => ({
                ...x,
                markers: (x.markers ?? []).filter((m) => m.id !== current.id),
              }));
              select(null);
            }}
          >
            {d.remove}
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={() => {
          const centre = def.area ? shapeCenter(def.area) : [1000, 1000];
          const marker = newMarker(def, centre[0], centre[1]);
          update((x) => ({ ...x, markers: [...(x.markers ?? []), marker] }));
          select({ kind: 'marker', id: marker.id });
        }}
      >
        {d.markerAdd}
      </button>
    </section>
  );
}

// ---------------------------------------------------------------- Forces

export function ForcesPanel({ def, update, selected, select, setTool }: PanelProps) {
  // The group presets of this mission's own mod set. An element may be one of
  // them instead of a CfgGroups class the game shipped, which is what lets a
  // mission field the group's own section rather than one of Bohemia's.
  const [groupPresets, setGroupPresets] = useState<ForcePresetView[]>([]);
  const modSetHash = def.terrain?.modSetHash ?? null;
  useEffect(() => {
    if (!modSetHash) {
      setGroupPresets([]);
      return;
    }
    let live = true;
    fetchForcePresets(modSetHash, 'group')
      .then((p) => live && setGroupPresets(p))
      .catch(() => live && setGroupPresets([]));
    return () => {
      live = false;
    };
  }, [modSetHash]);

  const forces = def.forces ?? [];
  const current = selected?.kind === 'force' ? forces.find((f) => f.id === selected.id) : null;
  // The script's own forces (ENGINE_DESIGN.md §2.20): their scene, posture and
  // post are read-only chips, because the script's phases and mechanics name them.
  const scripted = scriptedForceIds(def);
  // What the script owns of it (release D, D11.4): its name and, within its
  // scene's bounds, its size; nothing else, and it is never deleted.
  const locked = current ? ruleFor(def, '/forces', current) : null;
  const setForce = (id: string, patch: Partial<Force>) =>
    update((x) => ({
      ...x,
      forces: (x.forces ?? []).map((f) => (f.id === id ? { ...f, ...patch } : f)),
    }));

  return (
    <section className="card form" aria-label={d.forcesPanel}>
      <h2>{d.forcesPanel}</h2>
      <p className="muted small">{d.forcesHint}</p>
      {def.script && (
        <p className={`small ${activeGroups(def) > maxActiveGroups(def) ? 'error' : 'muted'}`}>
          {d.activeGroups(activeGroups(def), maxActiveGroups(def))}{' '}
          {activeGroups(def) >= maxActiveGroups(def) && d.activeGroupsFull}
        </p>
      )}
      {forces.length === 0 && <p className="muted small">{d.forceNone}</p>}
      <ul className="plain picker">
        {forces.map((f) => (
          <li key={f.id}>
            <button
              type="button"
              className={`row-button ${current?.id === f.id ? 'on' : ''}`}
              onClick={() => select({ kind: 'force', id: f.id })}
            >
              <span className="chip">{d.forceRoles[f.role]}</span>
              <span>{f.label || f.id}</span>
              {scripted.has(f.id) && <span className="chip">{ca.script.scripted}</span>}
            </button>
          </li>
        ))}
      </ul>

      {current && (
        <div className="stack">
          {scripted.has(current.id) && (
            <div className="stack" aria-label={ca.script.scripted}>
              <p className="muted small">{ca.script.scriptedForceHint}</p>
              <span className="row">
                {current.scene && (
                  <span className="chip">
                    {ca.script.forceScene(sceneLabel(current.scene, def.script))}
                  </span>
                )}
                {current.posture && (
                  <span className="chip">
                    {ca.script.forcePosture(ca.script.postures[current.posture] ?? current.posture)}
                  </span>
                )}
                {current.post && <span className="chip">{ca.script.forcePost(current.post)}</span>}
              </span>
            </div>
          )}
          <label>
            {d.forceLabel}
            <input
              value={current.label ?? ''}
              maxLength={60}
              onChange={(e) => setForce(current.id, { label: e.target.value })}
            />
          </label>
          {locked && (
            <div className="stack" aria-label={ca.design.readOnlyTitle}>
              {current.sizeRange ? (
                <>
                  <Num
                    label={d.forceSize}
                    value={current.size ?? current.sizeRange[0]}
                    min={current.sizeRange[0]}
                    max={current.sizeRange[1]}
                    onChange={(n) =>
                      setForce(current.id, {
                        size: Math.max(
                          current.sizeRange![0],
                          Math.min(current.sizeRange![1], Math.round(n)),
                        ),
                      })
                    }
                  />
                  <p className="muted small">
                    {d.forceSizeRange(current.sizeRange[0], current.sizeRange[1])}
                  </p>
                </>
              ) : (
                typeof current.size === 'number' && (
                  <p className="muted small">{d.forceSizeFixed(current.size)}</p>
                )
              )}
              <p className="muted small lock-note">
                <i className="bi bi-lock" aria-hidden="true" /> {locked.reason}
              </p>
            </div>
          )}
          {!locked && (
            <>
              <Choice
                label={d.forceRole}
                value={current.role}
                options={Object.entries(d.forceRoles)}
                onChange={(v) => setForce(current.id, { role: v as Force['role'] })}
              />
              <p className="muted small">{d.forceRoleHints[current.role]}</p>
              <div className="two">
                <Choice
                  label={d.forceSide}
                  value={current.side ?? 'east'}
                  options={Object.entries(d.forceSides)}
                  onChange={(v) => setForce(current.id, { side: v as Force['side'] })}
                />
                <Choice
                  label={d.forceCategory}
                  value={current.category ?? 'Infantry'}
                  options={Object.entries(d.forceCategories)}
                  onChange={(v) => setForce(current.id, { category: v })}
                />
              </div>
              <label>
                {d.forceFaction}
                <input
                  value={current.faction ?? ''}
                  onChange={(e) => setForce(current.id, { faction: e.target.value })}
                />
              </label>
              {current.role === 'static' || current.role === 'vehicle' ? (
                <label>
                  {d.forceVehicleClass}
                  <input
                    value={current.vehicleClass ?? ''}
                    onChange={(e) => setForce(current.id, { vehicleClass: e.target.value })}
                  />
                </label>
              ) : (
                <>
                  <label>
                    {d.forceGroupPreset}
                    <select
                      value={current.groupPresetId ?? ''}
                      onChange={(e) => setForce(current.id, { groupPresetId: e.target.value })}
                    >
                      <option value="">{d.forceGroupPresetNone}</option>
                      {groupPresets.map((g) => (
                        <option key={g.id} value={String(g.id)}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  {/*
                A preset wins over a class: the platform writes the preset's own
                men into the mission when it is published, and the CfgGroups
                template below is what is used when there is no preset.
              */}
                  <label>
                    {d.forceGroupClass}
                    <input
                      value={current.groupClass ?? ''}
                      disabled={!!current.groupPresetId}
                      onChange={(e) => setForce(current.id, { groupClass: e.target.value })}
                    />
                  </label>
                </>
              )}
              <div className="two">
                {current.role === 'static' ? (
                  <Num
                    label={d.forceCount}
                    value={current.count ?? 1}
                    min={1}
                    max={20}
                    onChange={(n) => setForce(current.id, { count: n })}
                  />
                ) : (
                  <Num
                    label={d.forceSize}
                    value={current.size ?? 0}
                    min={0}
                    max={24}
                    onChange={(n) => setForce(current.id, { size: n })}
                  />
                )}
                <Num
                  label={d.forceRadius}
                  value={current.radius ?? 0}
                  min={0}
                  max={5000}
                  onChange={(n) => setForce(current.id, { radius: n })}
                />
              </div>
              <div className="two">
                <Choice
                  label={d.forceBehaviour}
                  value={current.behaviour ?? 'SAFE'}
                  options={Object.entries(d.forceBehaviours)}
                  onChange={(v) => setForce(current.id, { behaviour: v as Force['behaviour'] })}
                />
                <Choice
                  label={d.forceStance}
                  value={current.stance ?? 'AUTO'}
                  options={Object.entries(d.forceStances)}
                  onChange={(v) => setForce(current.id, { stance: v as Force['stance'] })}
                />
              </div>
              <div className="two">
                <Choice
                  label={d.forceSpeed}
                  value={current.speed ?? 'NORMAL'}
                  options={Object.entries(d.forceSpeeds)}
                  onChange={(v) => setForce(current.id, { speed: v as Force['speed'] })}
                />
                <Num
                  label={d.forceSkill}
                  value={current.skill ?? 0.3}
                  min={0}
                  max={1}
                  step={0.05}
                  onChange={(n) => setForce(current.id, { skill: n })}
                />
              </div>

              {(current.role === 'guard' || current.role === 'static') && (
                <div className="row">
                  <span className="muted small">
                    {d.forceWatch}:{' '}
                    {current.watch && current.watch.length >= 2
                      ? `${Math.round(current.watch[0])}, ${Math.round(current.watch[1])}`
                      : d.none}
                  </span>
                  <button type="button" className="small" onClick={() => setTool('watch')}>
                    {d.forceWatchSet}
                  </button>
                </div>
              )}

              {(current.role === 'patrol' || current.role === 'vehicle') && (
                <div className="stack">
                  <strong>{d.forceRoute}</strong>
                  {(current.waypoints ?? []).length === 0 ? (
                    <p className="muted small">{d.forceRouteEmpty}</p>
                  ) : (
                    <ol className="plain numbered small">
                      {(current.waypoints ?? []).map((w, i) => (
                        <li key={i}>
                          {d.forceRoutePoint(i + 1)}: {Math.round(w[0])}, {Math.round(w[1])}
                        </li>
                      ))}
                    </ol>
                  )}
                  <div className="row">
                    <button type="button" className="small" onClick={() => setTool('route')}>
                      {d.forceRouteAdd}
                    </button>
                    <button
                      type="button"
                      className="small"
                      onClick={() => setForce(current.id, { waypoints: [] })}
                    >
                      {d.forceRouteClear}
                    </button>
                  </div>
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={current.cycle !== false}
                      onChange={(e) => setForce(current.id, { cycle: e.target.checked })}
                    />
                    {d.forceCycle}
                  </label>
                </div>
              )}

              {def.script &&
                current.side !== 'civilian' &&
                alarmScenes(def).map((scene) => (
                  <label className="check" key={scene.id}>
                    <input
                      type="checkbox"
                      checked={current.alertWith === `scene:${scene.id}`}
                      onChange={(e) =>
                        setForce(current.id, {
                          alertWith: e.target.checked ? `scene:${scene.id}` : undefined,
                        })
                      }
                    />
                    {d.forceAlertWith(scene.label || scene.id)}
                  </label>
                ))}
              {def.script && current.alertWith && (
                <p className="muted small">{d.forceAlertWithHint}</p>
              )}
              <Choice
                label={d.forceActivation}
                value={current.activation ?? 'onApproach'}
                options={[
                  ['onApproach', d.forceActivations.onApproach],
                  ['always', d.forceActivations.always],
                  ...def.objectives.map(
                    (o) =>
                      [`onObjective:${o.id}`, d.forceActivationOn(objectiveLabel(o))] as [
                        string,
                        string,
                      ],
                  ),
                ]}
                onChange={(v) => setForce(current.id, { activation: v })}
              />
              <Choice
                label={d.forceObjective}
                value={current.objectiveId ?? ''}
                options={[
                  ['', d.none],
                  ...def.objectives.map((o) => [o.id, objectiveLabel(o)] as [string, string]),
                ]}
                onChange={(v) => setForce(current.id, { objectiveId: v || undefined })}
              />
              <label className="check">
                <input
                  type="checkbox"
                  checked={current.reinforces === true}
                  onChange={(e) => setForce(current.id, { reinforces: e.target.checked })}
                />
                {d.forceReinforces}
              </label>

              <div className="row">
                <button
                  type="button"
                  className="small"
                  onClick={() => {
                    const copy: Force = {
                      ...current,
                      id: `${current.id}_2`,
                      position: [current.position[0] + 150, current.position[1] + 150],
                    };
                    // A duplicate whose id is taken gets the next free one.
                    const taken = new Set((def.forces ?? []).map((f) => f.id));
                    let id = copy.id;
                    let n = 2;
                    while (taken.has(id)) id = `${current.id}_${++n}`;
                    update((x) => ({ ...x, forces: [...(x.forces ?? []), { ...copy, id }] }));
                    select({ kind: 'force', id });
                  }}
                >
                  {d.forceDuplicate}
                </button>
                <button
                  type="button"
                  className="danger small"
                  onClick={() => {
                    update((x) => ({
                      ...x,
                      forces: (x.forces ?? []).filter((f) => f.id !== current.id),
                    }));
                    select(null);
                  }}
                >
                  {d.forceRemove}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- Composicions

/**
 * The compositions a mission places: a base at the insertion point, a
 * checkpoint on the road, sandbags round a gun.
 *
 * Eden places every sandbag and the browser could not place one at all
 * (AUTHORING_GAPS_STUDY.md §6.2). What is edited here is where one goes and how
 * it is turned; what it is made of lives in the catalogue, and the platform
 * writes the objects into the mission when it is published — so a published
 * mission is a fixed thing and a server that cannot reach the platform still
 * builds it.
 */
export function CompositionsPanel({ def, update, selected, select, setTool }: PanelProps) {
  const [presets, setPresets] = useState<ForcePresetView[]>([]);
  const modSetHash = def.terrain?.modSetHash ?? null;
  useEffect(() => {
    if (!modSetHash) {
      setPresets([]);
      return;
    }
    let live = true;
    fetchForcePresets(modSetHash, 'composition')
      .then((p) => live && setPresets(p))
      .catch(() => live && setPresets([]));
    return () => {
      live = false;
    };
  }, [modSetHash]);

  const placed = def.compositions ?? [];
  const current =
    selected?.kind === 'composition' ? placed.find((c) => c.id === selected.id) : null;
  const set = (id: string, patch: Partial<PlacedComposition>) =>
    update((x) => ({
      ...x,
      compositions: (x.compositions ?? []).map((c) => (c.id === id ? { ...c, ...patch } : c)),
    }));
  // A scene's composition (release D, D11.4): its name only, and it stays.
  const locked = current ? ruleFor(def, '/compositions', current) : null;

  return (
    <section className="card form" aria-label={d.panels.compositions}>
      <h2>{d.panels.compositions}</h2>
      <p className="muted small">{d.compositionsHint}</p>
      {presets.length === 0 && <p className="muted small">{d.compositionNoPresets}</p>}
      <ul className="list">
        {placed.map((c) => (
          <li
            key={c.id}
            className={selected?.kind === 'composition' && selected.id === c.id ? 'on' : ''}
          >
            <button
              type="button"
              className="grow"
              onClick={() => select({ kind: 'composition', id: c.id })}
            >
              <strong>{c.label || c.id}</strong>
              <span className={`chip side-${c.side ?? 'west'}`}>
                {ca.sideNames[c.side ?? 'west'] ?? c.side}
              </span>
              {c.objects && <span className="chip">{d.compositionObjects(c.objects.length)}</span>}
            </button>
            {!ruleFor(def, '/compositions', c) && (
              <button
                type="button"
                className="danger"
                aria-label={`${d.remove} ${c.label || c.id}`}
                onClick={() =>
                  update((x) => ({
                    ...x,
                    compositions: (x.compositions ?? []).filter((o) => o.id !== c.id),
                  }))
                }
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {placed.length === 0 && <p className="muted small">{d.compositionEmpty}</p>}
      <button type="button" onClick={() => setTool('composition')}>
        {d.compositionAdd}
      </button>

      {current && (
        <>
          <label>
            {d.compositionLabel}
            <input
              value={current.label ?? ''}
              onChange={(e) => set(current.id, { label: e.target.value })}
            />
          </label>
          {locked && (
            <p className="muted small lock-note">
              <i className="bi bi-lock" aria-hidden="true" /> {locked.reason}
            </p>
          )}
          {!locked && (
            <>
              <label>
                {d.compositionPreset}
                <select
                  value={current.presetId ?? ''}
                  onChange={(e) => set(current.id, { presetId: e.target.value })}
                >
                  <option value="">{d.compositionPresetNone}</option>
                  {presets.map((p) => (
                    <option key={p.id} value={String(p.id)}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="two">
                <Num
                  label={d.compositionAzimuth}
                  value={current.azimuth ?? 0}
                  min={0}
                  max={360}
                  onChange={(n) => set(current.id, { azimuth: n })}
                />
                <Num
                  label={d.compositionClear}
                  value={current.clearRadius ?? 0}
                  min={0}
                  max={200}
                  onChange={(n) => set(current.id, { clearRadius: n })}
                />
              </div>
              <div className="two">
                <Choice
                  label={d.compositionAnchor}
                  value={current.anchor ?? 'none'}
                  options={Object.entries(d.compositionAnchors)}
                  onChange={(v) => set(current.id, { anchor: v })}
                />
                <Choice
                  label={d.compositionSide}
                  value={current.side ?? 'west'}
                  options={Object.entries(d.forceSides)}
                  onChange={(v) => set(current.id, { side: v as PlacedComposition['side'] })}
                />
              </div>
              <Choice
                label={d.compositionAudience}
                value={current.audience ?? 'staff'}
                options={Object.entries(d.compositionAudiences)}
                onChange={(v) => set(current.id, { audience: v as PlacedComposition['audience'] })}
              />
              <label className="check">
                <input
                  type="checkbox"
                  checked={current.flatten !== false}
                  onChange={(e) => set(current.id, { flatten: e.target.checked })}
                />
                {d.compositionFlatten}
              </label>
            </>
          )}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- Civils

export function CiviliansPanel({ def, update, selected, select }: PanelProps) {
  const civilians = def.civilians ?? { enabled: false, zones: [] };
  const zones = civilians.zones ?? [];
  const setZone = (i: number, patch: Partial<CivilianZone>) =>
    update((x) => ({
      ...x,
      civilians: {
        ...(x.civilians ?? {}),
        zones: (x.civilians?.zones ?? []).map((z, j) => (j === i ? { ...z, ...patch } : z)),
      },
    }));

  return (
    <section className="card form" aria-label={d.civiliansPanel}>
      <h2>{d.civiliansPanel}</h2>
      <p className="muted small">{d.civiliansHint}</p>
      <label className="check">
        <input
          type="checkbox"
          checked={civilians.enabled === true}
          onChange={(e) =>
            update((x) => ({
              ...x,
              civilians: { ...(x.civilians ?? {}), enabled: e.target.checked },
            }))
          }
        />
        {d.civiliansOn}
      </label>
      {zones.length === 0 && <p className="muted small">{d.civilianZoneNone}</p>}
      {zones.map((z, i) => (
        <div
          className={`stack ${selected?.kind === 'civilian' && selected.id === z.id ? 'selected' : ''}`}
          key={z.id ?? i}
        >
          <label>
            {d.civilianLabel}
            <input
              value={z.label ?? ''}
              onFocus={() => select({ kind: 'civilian', id: z.id ?? '' })}
              onChange={(e) => setZone(i, { label: e.target.value })}
            />
          </label>
          <div className="two">
            <Num
              label={d.civilianRadius}
              value={z.radius}
              min={20}
              max={2000}
              onChange={(n) => setZone(i, { radius: n })}
            />
            <Num
              label={d.civilianDensity}
              value={z.density ?? 0.4}
              min={0}
              max={1}
              step={0.05}
              onChange={(n) => setZone(i, { density: n })}
            />
          </div>
          <Num
            label={d.civilianVehicles}
            value={z.vehicles ?? 0}
            min={0}
            max={1}
            step={0.05}
            onChange={(n) => setZone(i, { vehicles: n })}
          />
          <label className="check">
            <input
              type="checkbox"
              checked={z.panic !== false}
              onChange={(e) => setZone(i, { panic: e.target.checked })}
            />
            {d.civilianPanic}
          </label>
          <button
            type="button"
            className="danger small"
            onClick={() =>
              update((x) => ({
                ...x,
                civilians: {
                  ...(x.civilians ?? {}),
                  zones: (x.civilians?.zones ?? []).filter((_, j) => j !== i),
                },
              }))
            }
          >
            {d.remove}
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => {
          const centre = def.area ? shapeCenter(def.area) : [1000, 1000];
          const zone = newCivilianZone(def, centre[0], centre[1]);
          update((x) => ({
            ...x,
            civilians: { enabled: true, zones: [...(x.civilians?.zones ?? []), zone] },
          }));
          select({ kind: 'civilian', id: zone.id ?? '' });
        }}
      >
        {d.civilianZoneAdd}
      </button>
    </section>
  );
}

// ---------------------------------------------------------------- Intel

export function IntelPanel({ def, update, selected, select }: PanelProps) {
  const notes = def.intel ?? [];
  const current = selected?.kind === 'intel' ? notes.find((n) => n.id === selected.id) : null;
  const setNote = (id: string, patch: Partial<IntelNote>) =>
    update((x) => ({
      ...x,
      intel: (x.intel ?? []).map((n) => (n.id === id ? { ...n, ...patch } : n)),
    }));

  return (
    <section className="card form" aria-label={d.intelPanel}>
      <h2>{d.intelPanel}</h2>
      <p className="muted small">{d.intelHint}</p>
      {notes.length === 0 && <p className="muted small">{d.intelNone}</p>}
      <ul className="plain picker">
        {notes.map((n) => (
          <li key={n.id}>
            <button
              type="button"
              className={`row-button ${current?.id === n.id ? 'on' : ''}`}
              onClick={() => select({ kind: 'intel', id: n.id })}
            >
              <span className={`chip ${n.confidence ?? 'probable'}`}>
                {d.intelConfidences[n.confidence ?? 'probable']}
              </span>
              <span>{n.title || n.id}</span>
            </button>
          </li>
        ))}
      </ul>

      {current && (
        <div className="stack">
          <label>
            {d.intelTitle}
            <input
              value={current.title}
              maxLength={80}
              onChange={(e) => setNote(current.id, { title: e.target.value })}
            />
          </label>
          <label>
            {d.intelBody}
            <textarea
              rows={5}
              value={current.body ?? ''}
              onChange={(e) => setNote(current.id, { body: e.target.value })}
            />
          </label>
          <div className="two">
            <Choice
              label={d.intelConfidence}
              value={current.confidence ?? 'probable'}
              options={Object.entries(d.intelConfidences)}
              onChange={(v) => setNote(current.id, { confidence: v as IntelNote['confidence'] })}
            />
            <label>
              {d.intelSource}
              <input
                list="intel-sources"
                value={current.source ?? ''}
                onChange={(e) => setNote(current.id, { source: e.target.value })}
              />
              <datalist id="intel-sources">
                {d.intelSources.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </label>
          </div>
          <Choice
            label={d.intelMarker}
            value={current.markerId ?? ''}
            options={[
              ['', d.intelMarkerNone],
              ...(def.markers ?? []).map((m) => [m.id, m.text || m.id] as [string, string]),
            ]}
            onChange={(v) => setNote(current.id, { markerId: v || undefined })}
          />
          <Choice
            label={d.intelReveal}
            value={current.revealAt ?? 'briefing'}
            options={[
              ['briefing', d.intelRevealBriefing],
              ['start', d.intelRevealStart],
              ...def.objectives.map(
                (o) =>
                  [`onObjective:${o.id}`, d.markerRevealOn(objectiveLabel(o))] as [string, string],
              ),
              ['never', d.intelRevealNever],
            ]}
            onChange={(v) => setNote(current.id, { revealAt: v })}
          />
          <button
            type="button"
            className="danger"
            onClick={() => {
              update((x) => ({ ...x, intel: (x.intel ?? []).filter((n) => n.id !== current.id) }));
              select(null);
            }}
          >
            {d.intelRemove}
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={() => {
          const centre = def.area ? shapeCenter(def.area) : [1000, 1000];
          const note = newIntel(def, centre[0], centre[1]);
          update((x) => ({ ...x, intel: [...(x.intel ?? []), note] }));
          select({ kind: 'intel', id: note.id });
        }}
      >
        {d.intelAdd}
      </button>
    </section>
  );
}

// ---------------------------------------------------------------- Girs

/**
 * The turns the night takes. Every mission the group makes has two or three,
 * and they are always the same shape: a radio exchange with the higher command
 * and a fragmentary order in the log. The kind decides what actually changes.
 */
export function TwistsPanel({
  def,
  update,
  selected,
  select,
  accepted,
  busy = false,
  onToggle,
}: PanelProps & {
  /**
   * For a scripted mission, the twists its recipe accepts (ENGINE_DESIGN.md
   * §2.19, §2.20): only those are listed, and adding or dropping one generates
   * the mission again through `onToggle` instead of writing a twist by hand.
   * Undefined for every other mission, which keeps the free editor.
   */
  accepted?: OptionalView[];
  busy?: boolean;
  onToggle?: (optional: OptionalView) => void;
}) {
  const acceptedKeys = accepted?.map((o) => o.key);
  const twists = (def.twists ?? []).filter((t) => !acceptedKeys || acceptedKeys.includes(t.id));
  const current = selected?.kind === 'twist' ? twists.find((t) => t.id === selected.id) : null;
  const setTwist = (id: string, patch: Partial<Twist>) =>
    update((x) => ({
      ...x,
      twists: (x.twists ?? []).map((t) => (t.id === id ? { ...t, ...patch } : t)),
    }));
  const minutesOf = (at: string | undefined) => {
    const found = /^T\+([0-9]+)m$/.exec(at ?? '');
    return found ? Number(found[1]) : 20;
  };
  // On a scripted mission (release D, D11.4) a twist is a toggle above and its
  // text below: when it comes, what it does and whether it stays are the script's.
  const locked = current ? ruleFor(def, '/twists', current) : null;
  const phaseTitle = (id: string) =>
    (def.script?.phases ?? []).find((p) => p.id === id)?.title ?? id;

  return (
    <section className="card form" aria-label={d.twistsPanel}>
      <h2>{d.twistsPanel}</h2>
      <p className="muted small">{d.twistsHint}</p>
      {accepted && (
        <div className="stack" role="group" aria-label={ca.script.accepted}>
          <h3>{ca.script.accepted}</h3>
          <p className="muted small">{ca.script.acceptedHint}</p>
          {accepted.length === 0 && <p className="muted small">{ca.script.none}</p>}
          <ul className="plain stack">
            {accepted.map((o) => (
              <li key={o.key}>
                <label className="check">
                  <input
                    type="checkbox"
                    name={`accepted-${o.key}`}
                    checked={optionalOn(o, def)}
                    disabled={busy || !onToggle}
                    onChange={() => onToggle?.(o)}
                  />
                  <span>
                    <strong>{o.title}</strong>
                    <span className="muted small block">{o.blurb}</span>
                    {o.window && (
                      <span className="muted small block">{ca.script.window(o.window)}</span>
                    )}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
      {twists.length === 0 && <p className="muted small">{d.twistNone}</p>}
      <ul className="plain picker">
        {twists.map((t) => (
          <li key={t.id}>
            <button
              type="button"
              className={`row-button ${current?.id === t.id ? 'on' : ''}`}
              onClick={() => select({ kind: 'twist', id: t.id })}
            >
              <span className="chip">{d.twistKinds[t.kind] ?? t.kind}</span>
              <span>{t.title || t.id}</span>
            </button>
          </li>
        ))}
      </ul>

      {current && (
        <div className="stack">
          <label>
            {d.twistTitle}
            <input
              value={current.title}
              maxLength={80}
              onChange={(e) => setTwist(current.id, { title: e.target.value })}
            />
          </label>
          {locked && (
            <p className="muted small lock-note">
              <i className="bi bi-lock" aria-hidden="true" />{' '}
              {d.twistFixedWhen(d.twistAtWords(current.at, phaseTitle, current.until))}
            </p>
          )}
          {!locked && (
            <Choice
              label={d.twistKind}
              value={current.kind}
              options={Object.entries(d.twistKinds)}
              // Effects written for the old kind would override the new one's: dropped.
              onChange={(v) =>
                setTwist(current.id, { kind: v as Twist['kind'], effects: undefined })
              }
            />
          )}
          <label>
            {d.twistBody}
            <textarea
              rows={5}
              placeholder={d.twistBodyHint}
              value={current.body ?? ''}
              onChange={(e) => setTwist(current.id, { body: e.target.value })}
            />
          </label>
          <label>
            {d.twistRadio}
            <textarea
              rows={4}
              value={(current.radio ?? []).join('\n')}
              onChange={(e) =>
                setTwist(current.id, {
                  radio: e.target.value.split('\n').filter((line) => line.trim()),
                })
              }
            />
          </label>
          {!locked && (
            <>
              <Choice
                label={d.twistWhen}
                value={(current.at ?? 'start').startsWith('T+') ? 'T+' : (current.at ?? 'start')}
                options={[
                  ['start', d.twistWhenStart],
                  ['T+', d.twistWhenMinutes(minutesOf(current.at))],
                  ...def.objectives.map(
                    (o) =>
                      [`onObjective:${o.id}`, d.twistWhenOn(objectiveLabel(o))] as [string, string],
                  ),
                  ...(def.intel ?? []).map(
                    (n) =>
                      [`onIntel:${n.id}`, d.twistWhenIntel(n.title || n.id)] as [string, string],
                  ),
                  ['never', d.twistWhenNever],
                ]}
                onChange={(v) =>
                  setTwist(current.id, { at: v === 'T+' ? `T+${minutesOf(current.at)}m` : v })
                }
              />
              {(current.at ?? '').startsWith('T+') && (
                <Num
                  label={d.twistMinutes}
                  value={minutesOf(current.at)}
                  min={1}
                  max={180}
                  onChange={(n) => setTwist(current.id, { at: `T+${n}m` })}
                />
              )}
              {current.kind === 'weather' && (
                <Choice
                  label={d.twistWeather}
                  value={current.weather ?? 'fog'}
                  options={Object.entries(ca.weatherNames)}
                  onChange={(v) => setTwist(current.id, { weather: v })}
                />
              )}
              <Choice
                label={d.twistReveals}
                value={(current.reveals ?? [])[0] ?? ''}
                options={[
                  ['', d.twistNothing],
                  ...def.objectives.map((o) => [o.id, objectiveLabel(o)] as [string, string]),
                  ...(def.markers ?? []).map((m) => [m.id, m.text || m.id] as [string, string]),
                  ...(def.intel ?? []).map((n) => [n.id, n.title || n.id] as [string, string]),
                ]}
                onChange={(v) => setTwist(current.id, { reveals: v ? [v] : [] })}
              />
              <Choice
                label={d.twistCancels}
                value={(current.cancels ?? [])[0] ?? ''}
                options={[
                  ['', d.twistNothing],
                  ...def.objectives.map((o) => [o.id, objectiveLabel(o)] as [string, string]),
                ]}
                onChange={(v) => setTwist(current.id, { cancels: v ? [v] : [] })}
              />
              <button
                type="button"
                className="danger"
                onClick={() => {
                  update((x) => ({
                    ...x,
                    twists: (x.twists ?? []).filter((t) => t.id !== current.id),
                  }));
                  select(null);
                }}
              >
                {d.twistRemove}
              </button>
            </>
          )}
        </div>
      )}
      {/* A scripted mission takes only the twists its recipe accepts, above. */}
      {!accepted && mayAdd(def, '/twists') && (
        <button
          type="button"
          onClick={() => {
            const twist = newTwist(def);
            update((x) => ({ ...x, twists: [...(x.twists ?? []), twist] }));
            select({ kind: 'twist', id: twist.id });
          }}
        >
          {d.twistAdd}
        </button>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- Briefing

const SECTIONS = [
  'context',
  'warning',
  'situation',
  'mission',
  'execution',
  'support',
  'annexes',
] as const;

export function BriefingPanel({ def, update }: Pick<PanelProps, 'def' | 'update'>) {
  const briefing = def.briefing ?? {};
  const set = (key: (typeof SECTIONS)[number], value: string) =>
    update((x) => ({ ...x, briefing: { ...(x.briefing ?? {}), [key]: value } }));
  const empty = SECTIONS.every((k) => !briefing[k]?.trim());
  const howItWorks = briefing.howItWorks ?? [];
  return (
    <section className="card form" aria-label={d.briefingPanel}>
      <h2>{d.briefingPanel}</h2>
      {/* «Com funciona aquesta nit»: the platform writes it on every save, from the
          recipe's script and the definition; nobody types it (step D1.3). */}
      <section aria-label={d.briefingHowItWorks}>
        <h3>{d.briefingHowItWorks}</h3>
        <p className="muted small">{d.briefingHowItWorksHint}</p>
        {howItWorks.length === 0 ? (
          <p className="muted small">{d.briefingHowItWorksEmpty}</p>
        ) : (
          <ul className="how-it-works">
            {howItWorks.map((l) => (
              <li key={l.id}>
                {l.text}{' '}
                {l.generic && <span className="chip small">{d.briefingHowItWorksGeneric}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>
      <p className="muted small">{d.briefingHint}</p>
      {empty && def.summary && (
        <button type="button" onClick={() => set('situation', def.summary ?? '')}>
          {d.briefingFromSummary}
        </button>
      )}
      {SECTIONS.map((key) => (
        <label key={key}>
          {d.briefingSections[key]}
          <textarea
            rows={key === 'situation' || key === 'execution' ? 7 : 4}
            placeholder={d.briefingPlaceholders[key]}
            value={briefing[key] ?? ''}
            onChange={(e) => set(key, e.target.value)}
          />
        </label>
      ))}
    </section>
  );
}

// ---------------------------------------------------------------- Reaparició

/**
 * Whether anybody comes back and how (`settings.respawn`), one of the owner's
 * five ways or the server's default (2026-10-30). It is the model the
 * publisher and the runtime read; this only writes it.
 */
export function RespawnPanel({ def, update }: Pick<PanelProps, 'def' | 'update'>) {
  const r = d.respawnModel;
  const m = respawnOf(def.settings);
  const set = (patch: Partial<typeof m>) => {
    const next = { ...m, ...patch };
    update((x) => ({ ...x, settings: { ...(x.settings ?? {}), respawn: respawnNode(next) } }));
  };
  return (
    <section className="card form" aria-label={d.respawnRules}>
      <h2>{d.respawnRules}</h2>
      <p className="muted small">{r.hint}</p>
      <Choice
        label={r.mode}
        value={m.mode}
        options={RESPAWN_MODES.map((mode) => [mode, r.modes[mode]])}
        onChange={(v) => set({ mode: v as typeof m.mode })}
      />
      <p className="muted small">{r.modeHints[m.mode]}</p>
      {(m.mode === 'base' || m.mode === 'leader' || m.mode === 'lives') && (
        <Num
          label={r.delay}
          value={m.delay}
          min={0}
          max={900}
          onChange={(n) => set({ delay: n })}
        />
      )}
      {m.mode === 'lives' && (
        <div className="two">
          <Num
            label={r.lives}
            value={m.lives}
            min={1}
            max={99}
            onChange={(n) => set({ lives: n })}
          />
          <label className="check">
            <input
              type="checkbox"
              checked={m.shared}
              onChange={(e) => set({ shared: e.target.checked })}
            />
            <span>{r.shared}</span>
          </label>
        </div>
      )}
      {m.mode === 'waves' && (
        <Num
          label={r.waveMinutes}
          value={m.waveMinutes}
          min={1}
          max={30}
          onChange={(n) => set({ waveMinutes: n })}
        />
      )}
      {m.mode !== 'none' && <p className="muted small">{r.unseen}</p>}
    </section>
  );
}

// ---------------------------------------------------------------- Història

/** The mission's summary as text; a regeneration carries what is written here (D11.7). */
export function StoryPanel({
  def,
  update,
  code,
}: Pick<PanelProps, 'def' | 'update'> & { code: string }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  async function again() {
    setBusy(true);
    setNote(null);
    try {
      const { summary } = await rerollStory(code);
      update((x) => ({ ...x, summary }));
    } catch (err) {
      setNote(
        err instanceof ApiError && err.code === 'written_by_hand' ? d.storyByHand : d.rerollFailed,
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card form" aria-label={d.panels.story}>
      <h2>{d.panels.story}</h2>
      <p className="muted small">{d.storyHint}</p>
      <label>
        {d.storySummary}
        <textarea
          rows={10}
          placeholder={d.noStory}
          value={def.summary ?? ''}
          onChange={(e) => update((x) => ({ ...x, summary: e.target.value }))}
        />
      </label>
      <div className="row">
        <button type="button" disabled={busy || !def.summary} onClick={() => void again()}>
          {d.regenerateParagraph}
        </button>
      </div>
      {note && (
        <p className="muted small" role="status">
          {note}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- Repartiment

/** The cast: edit a person, lock him, draw his name again, add one (B5). */
export function CastPanel({
  def,
  update,
  code,
}: Pick<PanelProps, 'def' | 'update'> & { code: string }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const cast = def.cast ?? [];

  async function reroll(ids: string[]) {
    setBusy(ids.length === 1 ? ids[0] : '*');
    setNote(null);
    try {
      // One after the other: each rename reads the definition the one before left.
      for (const id of ids) {
        const { name } = await rerollCastName(code, id);
        update((x) => renameCast(x, id, name));
      }
    } catch {
      setNote(d.rerollFailed);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="card form" aria-label={d.panels.cast}>
      <h2>{d.panels.cast}</h2>
      <p className="muted small">{d.castHint}</p>
      {cast.length === 0 && <p className="muted small">{d.noCast}</p>}
      {cast.map((c) => (
        <div className="cast-card" key={c.id}>
          <label>
            {d.castName}
            <input
              key={c.name}
              defaultValue={c.name}
              disabled={!!c.locked}
              onBlur={(e) => {
                const name = e.target.value.trim();
                if (name && name !== c.name) update((x) => renameCast(x, c.id, name));
              }}
            />
          </label>
          <div className="two">
            <label>
              {d.castRole}
              <input
                value={c.role}
                onChange={(e) => update((x) => editCast(x, c.id, { role: e.target.value }))}
              />
            </label>
            <label>
              {d.castDisposition}
              <select
                value={c.disposition ?? ''}
                onChange={(e) =>
                  update((x) => editCast(x, c.id, { disposition: e.target.value || undefined }))
                }
              >
                <option value="">—</option>
                {Object.entries(d.dispositions).map(([k, name]) => (
                  <option key={k} value={k}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {/* A scripted person's scene role and mechanic: the script's, shown only. */}
          {(c.scene || c.sceneRole || c.mechanic) && (
            <span className="row">
              <span className="chip">{ca.script.scripted}</span>
              {(c.scene || c.sceneRole) && (
                <span className="chip">
                  {ca.script.castScene([c.scene, c.sceneRole].filter(Boolean).join(' · '))}
                </span>
              )}
              {c.mechanic && <span className="chip">{ca.script.castMechanic(c.mechanic)}</span>}
            </span>
          )}
          <div className="row">
            <button
              type="button"
              disabled={!!c.locked || busy !== null}
              onClick={() => void reroll([c.id])}
            >
              {d.reroll}
            </button>
            <button
              type="button"
              aria-pressed={!!c.locked}
              onClick={() =>
                update((x) => editCast(x, c.id, { locked: c.locked ? undefined : true }))
              }
            >
              {c.locked ? d.unlock : d.lock}
            </button>
            {canRemove(c.id) && (
              <button
                type="button"
                className="danger"
                onClick={() => update((x) => removeCast(x, c.id))}
              >
                {d.removeCastMember}
              </button>
            )}
          </div>
        </div>
      ))}
      <div className="row">
        <button type="button" onClick={() => update((x) => addCast(x, d.newCastName))}>
          {d.addCastMember}
        </button>
        {cast.length > 1 && (
          <button
            type="button"
            disabled={busy !== null || cast.every((c) => c.locked)}
            onClick={() => void reroll(cast.filter((c) => !c.locked).map((c) => c.id))}
          >
            {d.rerollAll}
          </button>
        )}
      </div>
      {note && (
        <p className="muted small" role="status">
          {note}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- Faccions

/**
 * The factions: who the players are and who the enemy is. Changing either generates the mission
 * again, since the forces, the vehicles and the names follow the faction.
 */
export function FactionsPanel({
  def,
  busy,
  onChange,
}: {
  def: Definition;
  busy?: boolean;
  /** Generate again with these parameters (`playerFaction`, `playerProfileId`, `enemyProfileId`). */
  onChange: (params: Record<string, unknown>) => void;
}) {
  const hash = def.modSet?.hash || def.terrain.modSetHash || '';
  const [profiles, setProfiles] = useState<FactionProfileView[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!hash) return;
    let live = true;
    fetchFactionProfiles(hash)
      .then((p) => live && setProfiles(p))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [hash]);

  const players = def.sides.players.faction;
  const enemy = def.sides.enemy.faction;
  const usable = (profiles ?? []).filter((p) => p.infantry !== false);
  const ours = usable.find((p) => p.factionClass === players);
  const theirs = usable.find((p) => p.factionClass === enemy);
  const [pickPlayers, setPickPlayers] = useState<number | null>(null);
  const [pickEnemy, setPickEnemy] = useState<number | null>(null);
  const chosenPlayers = usable.find((p) => p.id === pickPlayers);
  const chosenEnemy = usable.find((p) => p.id === pickEnemy);
  const changed =
    (!!chosenPlayers && chosenPlayers.factionClass !== players) ||
    (!!chosenEnemy && chosenEnemy.factionClass !== enemy);

  function apply() {
    const params: Record<string, unknown> = {};
    if (chosenPlayers && chosenPlayers.factionClass !== players) {
      params.playerFaction = chosenPlayers.factionClass;
      if (!chosenPlayers.derived) params.playerProfileId = chosenPlayers.id;
    }
    if (chosenEnemy && chosenEnemy.factionClass !== enemy) params.enemyProfileId = chosenEnemy.id;
    onChange(params);
  }

  const option = (p: FactionProfileView) => (
    <option key={p.id} value={p.id}>
      {p.name} ({p.side})
    </option>
  );

  return (
    <section className="card form" aria-label={d.panels.factions}>
      <h2>{d.panels.factions}</h2>
      <p className="muted small">{d.factionsHint}</p>
      <dl className="facts">
        <dt>{d.enemyStrength}</dt>
        <dd>{def.sides.enemy.strength ?? d.notGenerated}</dd>
      </dl>
      {profiles ? (
        <>
          <label>
            {d.playersFaction}
            <select
              value={pickPlayers ?? ours?.id ?? ''}
              onChange={(e) => setPickPlayers(Number(e.target.value))}
            >
              {!ours && <option value="">{players}</option>}
              {usable.map(option)}
            </select>
          </label>
          <label>
            {d.enemyFaction}
            <select
              value={pickEnemy ?? theirs?.id ?? ''}
              onChange={(e) => setPickEnemy(Number(e.target.value))}
            >
              {!theirs && <option value="">{enemy}</option>}
              {usable.map(option)}
            </select>
          </label>
          <p className="muted small">{d.factionsRegenerates}</p>
          <button type="button" disabled={!changed || busy} onClick={apply}>
            {d.factionsApply}
          </button>
        </>
      ) : (
        <>
          <dl className="facts">
            <dt>{d.playersFaction}</dt>
            <dd>{players}</dd>
            <dt>{d.enemyFaction}</dt>
            <dd>{enemy}</dd>
          </dl>
          {failed && <p className="muted small">{d.factionsUnavailable}</p>}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- Distància de visió

/** The view distance the mission recommends, in metres; blank leaves the server's own. */
export function ViewDistance({ def, update }: Pick<PanelProps, 'def' | 'update'>) {
  const value = (def.settings as { viewDistance?: number } | undefined)?.viewDistance;
  return (
    <label>
      {d.viewDistance}
      <input
        type="number"
        min={500}
        max={12000}
        step={100}
        value={value ?? ''}
        placeholder={d.viewDistanceDefault}
        onChange={(e) => {
          const n = e.target.value === '' ? undefined : Math.round(Number(e.target.value));
          update((x) => {
            const settings = { ...(x.settings ?? {}) } as Record<string, unknown>;
            if (n === undefined || !Number.isFinite(n)) delete settings.viewDistance;
            else settings.viewDistance = Math.min(12000, Math.max(500, n));
            return { ...x, settings };
          });
        }}
      />
      <span className="muted small">{d.viewDistanceHint}</span>
    </label>
  );
}

// ---------------------------------------------------------------- Comprovació

/**
 * Configuració: what the generator decided and an editor can still change.
 *
 * Until now this panel was a read-only list with a "planned" chip on it, so the
 * commonest edit anybody makes — the same mission, at dawn, with sixteen
 * players — could only be done by calling the API by hand
 * (AUTHORING_GAPS_STUDY.md §6.5).
 */
/** An hour as the designer writes it: 1.5 is 01:30. */
function hhmm(hour: number): string {
  const minutes = Math.round(hour * 60) % (24 * 60);
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

export function SetupPanel({
  def,
  update,
  fixed = [],
  constraints,
  recipeSupports,
  busy,
  onParams,
}: Pick<PanelProps, 'def' | 'update'> & {
  /** What the recipe fixes, each in words with its reason (release D, D11.4). */
  fixed?: { param: string; words: string; reason: string }[];
  /** What the recipe admits, for its parameters by regeneration (release D, D11.14). */
  constraints?: RecipeConstraints | null;
  /** The recipe's own supports, offered when its constraints name none. */
  recipeSupports?: Record<string, number>;
  /** A regeneration is running. */
  busy?: boolean;
  /** The recipe's parameters changed: the mission is generated again with them (D11.14). */
  onParams?: (params: Record<string, unknown>) => void;
}) {
  const time = def.time ?? {};
  // A scripted mission's hour, weather and date are the recipe's or a
  // regeneration's, and its enemy strength sizes every force: shown, not edited.
  const scriptedNight = isScripted(def);
  const clockLocked = !!pathRule(def, '/time/limitMinutes');
  const fixedOf = (param: string) => fixed.find((f) => f.param === param);
  const settings = (def.settings ?? {}) as Record<string, unknown>;
  const budget = (settings.budget ?? {}) as Record<string, number>;
  const setTime = (patch: Record<string, unknown>) =>
    update((prev) => ({ ...prev, time: { ...(prev.time ?? {}), ...patch } }));
  const setSetting = (patch: Record<string, unknown>) =>
    update((prev) => ({ ...prev, settings: { ...(prev.settings ?? {}), ...patch } }));
  const setBudget = (patch: Record<string, number>) =>
    update((prev) => ({
      ...prev,
      settings: {
        ...(prev.settings ?? {}),
        budget: { ...(((prev.settings ?? {}).budget as object) ?? {}), ...patch },
      },
    }));
  const setPlayers = (patch: Record<string, unknown>) =>
    update((prev) => ({
      ...prev,
      sides: { ...prev.sides, players: { ...prev.sides.players, ...patch } },
    }));

  return (
    <section className="card form" aria-label={d.panels.setup}>
      <h2>{d.panels.setup}</h2>
      <p className="muted small">{d.setupEditHint}</p>

      <dl className="facts">
        <dt>{d.terrain}</dt>
        <dd>
          {def.terrain.worldName} · {(def.terrain.worldSize ?? 20480) / 1000} km
        </dd>
      </dl>

      {!scriptedNight && (
        <div className="two">
          <Num
            label={d.playerSlots}
            value={def.sides.players.slots}
            min={2}
            max={64}
            onChange={(n) => setPlayers({ slots: Math.max(2, Math.min(64, Math.round(n))) })}
          />
          <Num
            label={d.squadSize}
            value={def.sides.players.squadSize ?? 8}
            min={2}
            max={12}
            onChange={(n) => setPlayers({ squadSize: Math.max(2, Math.min(12, Math.round(n))) })}
          />
        </div>
      )}
      {!scriptedNight && <p className="muted small">{d.squadSizeHint}</p>}

      {scriptedNight && (
        <div className="stack" role="group" aria-label={d.fixedTitle}>
          <h3>{d.fixedTitle}</h3>
          <dl className="facts">
            {(
              [
                ['hour', d.fixedHour, time.hour != null ? hhmm(time.hour) : '—'],
                [
                  'weather',
                  d.fixedWeather,
                  ca.weatherNames[time.weather ?? 'clear'] ?? time.weather,
                ],
                ['date', d.fixedDate, time.date ?? '—'],
              ] as [string, string, string][]
            )
              // The hour and the date the type leaves open are parameters below (D11.14).
              .filter(([param]) => param === 'weather' || !!fixedOf(param))
              .map(([param, label, value]) => (
                <div key={param}>
                  <dt>{label}</dt>
                  <dd>
                    {value}
                    {fixedOf(param) && (
                      <span className="muted small block">{fixedOf(param)!.words}</span>
                    )}
                    <span className="muted small block">
                      <i className="bi bi-lock" aria-hidden="true" />{' '}
                      {fixedOf(param)?.reason ?? d.fixedByRegeneration}
                    </span>
                  </dd>
                </div>
              ))}
          </dl>
        </div>
      )}
      {scriptedNight && (
        <RecipeParams
          key={def.revision}
          def={def}
          constraints={constraints}
          fixed={fixed}
          recipeSupports={recipeSupports}
          busy={busy}
          onRegenerate={onParams}
        />
      )}
      {!scriptedNight && (
        <div className="two">
          <Num
            label={d.timeOfDay}
            value={time.hour}
            min={0}
            max={24}
            step={0.5}
            onChange={(n) => setTime({ hour: Math.max(0, Math.min(24, n)) })}
          />
          <label>
            {d.date}
            <input
              type="date"
              value={time.date ?? ''}
              onChange={(e) => setTime({ date: e.target.value })}
            />
          </label>
        </div>
      )}
      {!scriptedNight && <p className="muted small">{d.dateHint}</p>}

      <div className="two">
        {!scriptedNight && (
          <Choice
            label={d.weather}
            value={time.weather ?? 'clear'}
            options={Object.entries(ca.weatherNames)}
            onChange={(v) => setTime({ weather: v })}
          />
        )}
        {!clockLocked && (
          <Num
            label={d.limitMinutes}
            value={time.limitMinutes ?? 0}
            min={0}
            max={480}
            step={5}
            onChange={(n) => setTime({ limitMinutes: Math.max(0, Math.round(n)) })}
          />
        )}
      </div>
      {!clockLocked && <p className="muted small">{d.limitHint}</p>}

      {!scriptedNight && (
        <Choice
          label={d.enemyStrength}
          value={def.sides.enemy.strength ?? 'medium'}
          options={Object.entries(d.enemyStrengthNames)}
          onChange={(v) =>
            update((prev) => ({
              ...prev,
              sides: { ...prev.sides, enemy: { ...prev.sides.enemy, strength: v } },
            }))
          }
        />
      )}
      {/* What the word means in groups on the ground: the generated enemy is a
          number in the definition and a spawn at runtime, so the designer's map
          shows nothing and "is anybody out there?" was a fair question. */}
      <p className="muted small">{d.enemyStrengthHint}</p>

      <div className="two">
        <Num
          label={d.maxActiveGroups}
          value={budget.maxActiveGroups ?? 30}
          min={1}
          max={200}
          onChange={(n) => setBudget({ maxActiveGroups: Math.max(1, Math.round(n)) })}
        />
        <Num
          label={d.activationRadius}
          value={budget.activationRadius ?? 1000}
          min={200}
          max={5000}
          step={50}
          onChange={(n) => setBudget({ activationRadius: Math.max(200, Math.round(n)) })}
        />
      </div>
      <Num
        label={d.headlessClients}
        value={budget.headlessClients ?? 1}
        min={0}
        max={3}
        onChange={(n) => setBudget({ headlessClients: Math.max(0, Math.min(3, Math.round(n))) })}
      />

      <label className="check">
        <input
          type="checkbox"
          checked={settings.civilianPresence !== false}
          onChange={(e) => setSetting({ civilianPresence: e.target.checked })}
        />
        {d.civilianPresence}
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={settings.zeusForCurator !== false}
          onChange={(e) => setSetting({ zeusForCurator: e.target.checked })}
        />
        {d.zeusForCurator}
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={settings.dryRun === true}
          onChange={(e) => setSetting({ dryRun: e.target.checked })}
        />
        {d.dryRun}
      </label>
      <p className="muted small">{d.dryRunHint}</p>

      <h3>{d.points}</h3>
      <p className="muted small">{d.intentHint}</p>
      {(['insertion', 'extraction'] as const).map((which) => {
        const point = def.points?.[which];
        return (
          <div className="stack" key={which}>
            <strong>{which === 'insertion' ? d.insertionPoint : d.extractionPoint}</strong>
            {scriptedNight ? (
              <p className="muted small">
                {ca.design.recipeParams.meansNames[point?.means ?? 'foot'] ?? point?.means}
              </p>
            ) : (
              <Choice
                label={d.means}
                value={point?.means ?? 'foot'}
                options={Object.entries(d.meansNames)}
                onChange={(v) =>
                  update((prev) => ({
                    ...prev,
                    points: {
                      ...(prev.points ?? {}),
                      [which]: { ...(prev.points?.[which] ?? {}), means: v as never },
                    },
                  }))
                }
              />
            )}
            <p className="muted small">
              {point?.position
                ? `${Math.round(point.position[0])}, ${Math.round(point.position[1])}`
                : '—'}
            </p>
          </div>
        );
      })}
    </section>
  );
}

export function CheckPanel({
  def,
  select,
  setPanel,
  stored = [],
  preflight,
}: {
  def: Definition;
  select: (s: Selection | null) => void;
  setPanel: (panel: string) => void;
  /** What the platform found in the saved revision; warnings while its check only warns. */
  stored?: DefinitionRefusal[];
  /** What the platform would move before play (a dry run on the saved revision). */
  preflight?: PreflightReport | null;
}) {
  const rowOf = (r: PreflightRow, level: Check['level'], text: string): Check => {
    const kind = r.kind === 'waypoint' ? 'force' : r.kind;
    const id = r.kind === 'waypoint' ? r.id.split('#')[0] : r.id;
    const selectable = ['objective', 'force', 'composition', 'intel', 'civilian'].includes(kind);
    return {
      level,
      text,
      select: selectable ? { kind: kind as Selection['kind'], id } : undefined,
    };
  };
  const words = (r: PreflightRow) =>
    [d.preflightKinds[r.kind] ?? r.kind, r.label, d.preflightChecks[r.check] ?? r.check] as const;
  const found: Check[] = [
    ...checks(def),
    ...stored.map((r): Check => ({ level: 'warning', text: d.checkStored(r.reason) })),
    ...(preflight?.unplaceable ?? []).map((r) =>
      rowOf(r, 'error', d.preflightUnplaceable(...words(r))),
    ),
    ...(preflight?.moved ?? []).map((r) =>
      rowOf(r, 'warning', d.preflightMoved(...words(r), r.metres)),
    ),
    ...(preflight?.left ?? []).map((r) => rowOf(r, 'warning', d.preflightLeft(...words(r)))),
  ];
  const errors = found.filter((c) => c.level === 'error');
  const warnings = found.filter((c) => c.level === 'warning');
  const panelOf: Record<string, string> = {
    objective: 'objectives',
    marker: 'markers',
    force: 'forces',
    composition: 'compositions',
    intel: 'intel',
    civilian: 'civilians',
    zone: 'area',
    area: 'area',
    start: 'setup',
    twist: 'twists',
  };
  return (
    <section className="card" aria-label={d.panels.check}>
      <h2>{d.panels.check}</h2>
      {found.length === 0 && <p className="muted small">{d.checkNone}</p>}
      {found.length > 0 && (
        <p className="muted small">
          {d.checkErrors(errors.length)} · {d.checkWarnings(warnings.length)}
        </p>
      )}
      <ul className="plain checks">
        {[...errors, ...warnings].map((c, i) => (
          <li key={i} className={c.level}>
            <span>{c.text}</span>
            {c.select && (
              <button
                type="button"
                className="small"
                onClick={() => {
                  select(c.select ?? null);
                  setPanel(panelOf[c.select!.kind] ?? 'objectives');
                }}
              >
                {d.checkGoTo}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

// ------------------------------------------------------------------ Portada

/**
 * The picture the mission is shown with, in the Configuració panel beside
 * everything else about the mission that is not the mission itself.
 *
 * Three choices, each shown as what it will actually draw, because the words
 * "automàtica" and "el terreny" mean nothing until you see which of them has a
 * picture for this world. Nothing is uploaded: the platform draws the terrain
 * it already caches or a tile from the mission's own type, so there is no file
 * store to keep, no size to police and nothing of anybody's in it. It is not a
 * patch on the definition — no cover ever reaches the game — so it saves at
 * once and does not move the revision the editors are working on.
 */
export function CoverPanel({
  mission,
  onPick,
}: {
  mission: MissionView;
  onPick: (choice: Cover) => void;
}) {
  const chosen = (mission.cover ?? 'AUTO') as Cover;
  const t = ca.missionUi.cover;
  return (
    <section className="card form" aria-label={t.title}>
      <h2>{t.title}</h2>
      <p className="muted small">{t.hint}</p>
      <div className="cards grid pick covers" role="radiogroup" aria-label={t.title}>
        {COVERS.map((choice) => (
          <button
            type="button"
            key={choice}
            className={`card ${chosen === choice ? 'selected' : ''}`}
            role="radio"
            aria-checked={chosen === choice}
            onClick={() => onPick(choice)}
          >
            <MissionCover
              world={mission.worldName}
              worldLabel={mission.worldDisplayName ?? mission.worldName}
              recipe={mission.recipe}
              recipeIcon={mission.recipeIcon}
              recipeTitle={mission.recipeTitle ?? mission.recipe}
              cover={choice}
            />
            <strong>{t[choice]}</strong>
            <span className="muted small">{t[`${choice}Hint`]}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
